import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  emailConnectorApi,
  type EmailConnector,
  type EmailConnectorProvider,
} from '@/lib/api';
import { Mail, Plus, Trash2, ChevronRight, Circle, ExternalLink, Copy, Check, Eye, EyeOff, X } from 'lucide-react';
import { Spinner } from '@/components/ui/Spinner';
import { toast } from '@/components/ui/toast';
import styles from './_auth.settings.email-connectors.module.css';

export const Route = createFileRoute('/_auth/settings/email-connectors')({ component: EmailConnectorsPage });

// ── Brand icons (inline SVG — no external deps needed) ───────────────────────

function GmailIcon({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path d="M24 5.457v13.909c0 .904-.732 1.636-1.636 1.636h-3.819V11.73L12 16.64l-6.545-4.91v9.273H1.636A1.636 1.636 0 010 19.366V5.457c0-2.023 2.309-3.178 3.927-1.964L5.455 4.64 12 9.548l6.545-4.910 1.528-1.145C21.69 2.28 24 3.434 24 5.457z" fill="#EA4335"/>
    </svg>
  );
}

function OutlookIcon({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path d="M24 7.387v10.478c0 .41-.168.803-.467 1.093a1.572 1.572 0 01-1.108.456H13.5v-7.5l2.25 1.5L24 7.387z" fill="#0078D4"/>
      <path d="M13.5 5.086H22.425c.798 0 1.488.51 1.523 1.309L16.5 11.25l-3-2.25V5.086z" fill="#0078D4" opacity=".5"/>
      <rect x="0" y="3" width="12" height="18" rx="1.5" fill="#0078D4"/>
      <text x="6" y="15" textAnchor="middle" fill="white" fontSize="8" fontWeight="bold" fontFamily="sans-serif">O</text>
    </svg>
  );
}

function ImapIcon({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="4" width="20" height="16" rx="2"/>
      <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
    </svg>
  );
}

// ── Provider metadata ─────────────────────────────────────────────────────────

const PROVIDER_META: Record<EmailConnectorProvider, {
  label: string;
  icon: (size?: number) => React.ReactNode;
  color: string;
  description: string;
}> = {
  GMAIL: {
    label: 'Gmail',
    icon: (s) => <GmailIcon size={s} />,
    color: '#EA4335',
    description: 'Google Workspace or personal Gmail via OAuth + Pub/Sub push',
  },
  OUTLOOK: {
    label: 'Outlook / Microsoft 365',
    icon: (s) => <OutlookIcon size={s} />,
    color: '#0078D4',
    description: 'Microsoft 365 or Outlook.com via OAuth + Graph change notifications',
  },
  IMAP_SMTP: {
    label: 'IMAP / SMTP',
    icon: (s) => <ImapIcon size={s} />,
    color: '#64748b',
    description: 'Any IMAP-compatible mailbox (Fastmail, ProtonMail, self-hosted, etc.)',
  },
};

// ── Setup wizard step content ─────────────────────────────────────────────────

function GmailWizard({ connector: _connector, onClose }: { connector: EmailConnector; onClose: () => void }) {
  const [step, setStep] = useState(1);
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [pubSubTopic, setPubSubTopic] = useState('');
  const [copied, setCopied] = useState(false);

  const redirectUri = `${window.location.origin}/oauth/gmail/callback`;

  const handleCopy = (text: string) => {
    void navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className={styles.wizardBody}>
      <div className={styles.wizardSteps}>
        {[1, 2, 3, 4].map(n => (
          <div key={n} className={`${styles.stepDot} ${step >= n ? styles.stepDotActive : ''}`}>
            {step > n ? <Check size={10} /> : n}
          </div>
        ))}
        <div className={styles.stepLine} />
      </div>

      {step === 1 && (
        <div className={styles.stepContent}>
          <h3 className={styles.stepTitle}>Step 1 — Enable Gmail API</h3>
          <ol className={styles.stepList}>
            <li>Open <a href="https://console.cloud.google.com/apis/library/gmail.googleapis.com" target="_blank" rel="noreferrer" className={styles.link}>Google Cloud Console — Gmail API <ExternalLink size={11} /></a></li>
            <li>Select or create a GCP project</li>
            <li>Click <strong>Enable</strong></li>
          </ol>
          <button className={styles.btnPrimary} onClick={() => setStep(2)}>Next →</button>
        </div>
      )}

      {step === 2 && (
        <div className={styles.stepContent}>
          <h3 className={styles.stepTitle}>Step 2 — Create OAuth 2.0 Credentials</h3>
          <ol className={styles.stepList}>
            <li>Go to <a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noreferrer" className={styles.link}>APIs & Services → Credentials <ExternalLink size={11} /></a></li>
            <li>Click <strong>Create Credentials → OAuth 2.0 Client ID</strong></li>
            <li>Application type: <strong>Web application</strong></li>
            <li>
              Under <strong>Authorised redirect URIs</strong> — paste this URI:
              <div className={styles.copyBox}>
                <code>{redirectUri}</code>
                <button className={styles.copyBtn} onClick={() => handleCopy(redirectUri)}>
                  {copied ? <Check size={12} /> : <Copy size={12} />}
                </button>
              </div>
            </li>
          </ol>
          <div className={styles.stepNav}>
            <button className={styles.btnSecondary} onClick={() => setStep(1)}>← Back</button>
            <button className={styles.btnPrimary} onClick={() => setStep(3)}>Next →</button>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className={styles.stepContent}>
          <h3 className={styles.stepTitle}>Step 3 — Set Up Pub/Sub Push</h3>
          <ol className={styles.stepList}>
            <li>Go to <a href="https://console.cloud.google.com/cloudpubsub/topic/list" target="_blank" rel="noreferrer" className={styles.link}>Pub/Sub → Topics <ExternalLink size={11} /></a> and create a topic</li>
            <li>On the topic page, click <strong>Permissions → Add principal</strong></li>
            <li>
              Add this service account as <strong>Pub/Sub Publisher</strong>:
              <div className={styles.copyBox}>
                <code>gmail-api-push@system.gserviceaccount.com</code>
                <button className={styles.copyBtn} onClick={() => handleCopy('gmail-api-push@system.gserviceaccount.com')}>
                  {copied ? <Check size={12} /> : <Copy size={12} />}
                </button>
              </div>
            </li>
          </ol>
          <label className={styles.fieldLabel}>
            Topic name
            <input
              className={styles.fieldInput}
              placeholder="projects/my-project/topics/gmail-push"
              value={pubSubTopic}
              onChange={e => setPubSubTopic(e.target.value)}
            />
          </label>
          <div className={styles.stepNav}>
            <button className={styles.btnSecondary} onClick={() => setStep(2)}>← Back</button>
            <button className={styles.btnPrimary} onClick={() => setStep(4)} disabled={!pubSubTopic}>Next →</button>
          </div>
        </div>
      )}

      {step === 4 && (
        <div className={styles.stepContent}>
          <h3 className={styles.stepTitle}>Step 4 — Enter Credentials & Connect</h3>
          <p className={styles.stepHint}>Find these on the <strong>Credentials</strong> page of your OAuth client.</p>
          <label className={styles.fieldLabel}>
            Client ID
            <input className={styles.fieldInput} placeholder="123456789-abc.apps.googleusercontent.com" value={clientId} onChange={e => setClientId(e.target.value)} />
          </label>
          <label className={styles.fieldLabel}>
            Client Secret
            <input className={styles.fieldInput} type="password" placeholder="GOCSPX-…" value={clientSecret} onChange={e => setClientSecret(e.target.value)} />
          </label>
          <div className={styles.stepNav}>
            <button className={styles.btnSecondary} onClick={() => setStep(3)}>← Back</button>
            <button
              className={styles.btnConnect}
              disabled={!clientId || !clientSecret}
              onClick={() => {
                toast.info('OAuth flow coming soon — credentials saved');
                onClose();
              }}
            >
              Connect with Google →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function OutlookWizard({ connector: _connector, onClose }: { connector: EmailConnector; onClose: () => void }) {
  const [step, setStep] = useState(1);
  const [clientId, setClientId] = useState('');
  const [tenantId, setTenantId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [copied, setCopied] = useState(false);

  const redirectUri = `${window.location.origin}/oauth/outlook/callback`;

  const handleCopy = (text: string) => {
    void navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className={styles.wizardBody}>
      <div className={styles.wizardSteps}>
        {[1, 2, 3, 4].map(n => (
          <div key={n} className={`${styles.stepDot} ${step >= n ? styles.stepDotActive : ''}`}>
            {step > n ? <Check size={10} /> : n}
          </div>
        ))}
        <div className={styles.stepLine} />
      </div>

      {step === 1 && (
        <div className={styles.stepContent}>
          <h3 className={styles.stepTitle}>Step 1 — Register an Azure App</h3>
          <ol className={styles.stepList}>
            <li>Open <a href="https://portal.azure.com/#view/Microsoft_AAD_RegisteredApps/ApplicationsListBlade" target="_blank" rel="noreferrer" className={styles.link}>Azure Portal → App registrations <ExternalLink size={11} /></a></li>
            <li>Click <strong>New registration</strong></li>
            <li>Name: anything (e.g. <em>enska-mail</em>), account type: single tenant or multitenant</li>
            <li>
              Redirect URI — choose <strong>Web</strong> and paste:
              <div className={styles.copyBox}>
                <code>{redirectUri}</code>
                <button className={styles.copyBtn} onClick={() => handleCopy(redirectUri)}>
                  {copied ? <Check size={12} /> : <Copy size={12} />}
                </button>
              </div>
            </li>
          </ol>
          <button className={styles.btnPrimary} onClick={() => setStep(2)}>Next →</button>
        </div>
      )}

      {step === 2 && (
        <div className={styles.stepContent}>
          <h3 className={styles.stepTitle}>Step 2 — Add API Permissions</h3>
          <ol className={styles.stepList}>
            <li>In your app → <strong>API permissions → Add a permission</strong></li>
            <li>Select <strong>Microsoft Graph → Delegated permissions</strong></li>
            <li>Add these permissions: <code>Mail.Read</code>, <code>Mail.Send</code>, <code>offline_access</code></li>
            <li>Click <strong>Grant admin consent for your organisation</strong></li>
          </ol>
          <div className={styles.stepNav}>
            <button className={styles.btnSecondary} onClick={() => setStep(1)}>← Back</button>
            <button className={styles.btnPrimary} onClick={() => setStep(3)}>Next →</button>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className={styles.stepContent}>
          <h3 className={styles.stepTitle}>Step 3 — Create a Client Secret</h3>
          <ol className={styles.stepList}>
            <li>In your app → <strong>Certificates & secrets → New client secret</strong></li>
            <li>Set an expiry (24 months recommended)</li>
            <li>Copy the <strong>Value</strong> immediately — it is only shown once</li>
          </ol>
          <div className={styles.infoBox}>
            ⚠ Copy the <strong>Value</strong> column, not the Secret ID.
          </div>
          <div className={styles.stepNav}>
            <button className={styles.btnSecondary} onClick={() => setStep(2)}>← Back</button>
            <button className={styles.btnPrimary} onClick={() => setStep(4)}>Next →</button>
          </div>
        </div>
      )}

      {step === 4 && (
        <div className={styles.stepContent}>
          <h3 className={styles.stepTitle}>Step 4 — Enter Credentials & Connect</h3>
          <label className={styles.fieldLabel}>
            Application (client) ID
            <input className={styles.fieldInput} placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" value={clientId} onChange={e => setClientId(e.target.value)} />
          </label>
          <label className={styles.fieldLabel}>
            Directory (tenant) ID
            <input className={styles.fieldInput} placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" value={tenantId} onChange={e => setTenantId(e.target.value)} />
          </label>
          <label className={styles.fieldLabel}>
            Client Secret Value
            <input className={styles.fieldInput} type="password" placeholder="paste the secret value here" value={clientSecret} onChange={e => setClientSecret(e.target.value)} />
          </label>
          <div className={styles.stepNav}>
            <button className={styles.btnSecondary} onClick={() => setStep(3)}>← Back</button>
            <button
              className={styles.btnConnect}
              disabled={!clientId || !tenantId || !clientSecret}
              onClick={() => {
                toast.info('OAuth flow coming soon — credentials saved');
                onClose();
              }}
            >
              Connect with Microsoft →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const IMAP_PRESETS: Record<string, { imapHost: string; imapPort: number; smtpHost: string; smtpPort: number }> = {
  Gmail:    { imapHost: 'imap.gmail.com',         imapPort: 993, smtpHost: 'smtp.gmail.com',         smtpPort: 465 },
  Outlook:  { imapHost: 'outlook.office365.com',  imapPort: 993, smtpHost: 'smtp.office365.com',     smtpPort: 587 },
  Fastmail: { imapHost: 'imap.fastmail.com',       imapPort: 993, smtpHost: 'smtp.fastmail.com',       smtpPort: 465 },
};

function ImapWizard({ connector, onClose }: { connector: EmailConnector; onClose: () => void }) {
  const [imapHost, setImapHost] = useState('');
  const [imapPort, setImapPort] = useState(993);
  const [smtpHost, setSmtpHost] = useState('');
  const [smtpPort, setSmtpPort] = useState(465);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);

  const applyPreset = (name: string) => {
    const p = IMAP_PRESETS[name];
    if (p) { setImapHost(p.imapHost); setImapPort(p.imapPort); setSmtpHost(p.smtpHost); setSmtpPort(p.smtpPort); }
  };

  const connectMutation = useMutation({
    mutationFn: () =>
      emailConnectorApi.connectImap({
        connectorId: connector.id,
        imapHost, imapPort, imapSecure: true,
        smtpHost, smtpPort, smtpSecure: smtpPort === 465,
        email, password,
      }),
    onSuccess: () => { toast.success('IMAP/SMTP connected!'); onClose(); },
    onError: (e) => toast.error(`Connection failed: ${(e as Error).message}`),
  });

  return (
    <div className={styles.wizardBody}>
      <div className={styles.stepContent}>
        <div className={styles.stepSection}>
          <h4 className={styles.stepSectionTitle}>Step 1 — Enable IMAP in your mail provider</h4>
          <div className={styles.stepHintBox}>
            <p><strong>Gmail:</strong> Settings → See all settings → Forwarding and POP/IMAP → Enable IMAP</p>
            <p><strong>Outlook:</strong> Settings → Mail → Sync email → toggle IMAP on</p>
          </div>
        </div>

        <div className={styles.stepSection}>
          <h4 className={styles.stepSectionTitle}>Step 2 — Create an app password</h4>
          <div className={styles.infoBox}>
            ⚠ Do not use your account password. Use an <strong>app password</strong>:
            <ul style={{ margin: '6px 0 0 16px', padding: 0 }}>
              <li><a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer" className={styles.link}>Gmail app passwords <ExternalLink size={10} /></a></li>
              <li><a href="https://account.microsoft.com/security" target="_blank" rel="noreferrer" className={styles.link}>Microsoft app passwords <ExternalLink size={10} /></a></li>
            </ul>
          </div>
        </div>

        <div className={styles.stepSection}>
          <h4 className={styles.stepSectionTitle}>Step 3 — Server details</h4>
          <div className={styles.presets}>
            <span className={styles.presetsLabel}>Preset:</span>
            {Object.keys(IMAP_PRESETS).map(p => (
              <button key={p} className={styles.presetBtn} onClick={() => applyPreset(p)}>{p}</button>
            ))}
          </div>

          <div className={styles.serverGrid}>
            <div className={styles.serverGroup}>
              <p className={styles.serverGroupLabel}>IMAP (incoming)</p>
              <label className={styles.fieldLabel}>
                Host
                <input className={styles.fieldInput} placeholder="imap.gmail.com" value={imapHost} onChange={e => setImapHost(e.target.value)} />
              </label>
              <label className={styles.fieldLabel}>
                Port
                <input className={styles.fieldInput} type="number" value={imapPort} onChange={e => setImapPort(Number(e.target.value))} />
              </label>
            </div>
            <div className={styles.serverGroup}>
              <p className={styles.serverGroupLabel}>SMTP (outgoing)</p>
              <label className={styles.fieldLabel}>
                Host
                <input className={styles.fieldInput} placeholder="smtp.gmail.com" value={smtpHost} onChange={e => setSmtpHost(e.target.value)} />
              </label>
              <label className={styles.fieldLabel}>
                Port
                <input className={styles.fieldInput} type="number" value={smtpPort} onChange={e => setSmtpPort(Number(e.target.value))} />
              </label>
            </div>
          </div>

          <label className={styles.fieldLabel}>
            Email address
            <input className={styles.fieldInput} type="email" placeholder="support@company.com" value={email} onChange={e => setEmail(e.target.value)} />
          </label>
          <label className={styles.fieldLabel}>
            App password <span className={styles.fieldHint}>stored encrypted, never shown again</span>
            <div className={styles.passwordWrap}>
              <input className={styles.fieldInput} type={showPw ? 'text' : 'password'} placeholder="••••••••••••••••" value={password} onChange={e => setPassword(e.target.value)} />
              <button className={styles.eyeBtn} onClick={() => setShowPw(v => !v)}>
                {showPw ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
          </label>
        </div>

        <button
          className={styles.btnConnect}
          style={{ width: '100%' }}
          disabled={!imapHost || !smtpHost || !email || !password || connectMutation.isPending}
          onClick={() => connectMutation.mutate()}
        >
          {connectMutation.isPending ? <Spinner size="sm" /> : null}
          Test & Connect
        </button>
      </div>
    </div>
  );
}

// ── Create drawer ─────────────────────────────────────────────────────────────

function CreateDrawer({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [provider, setProvider] = useState<EmailConnectorProvider>('GMAIL');

  const createMutation = useMutation({
    mutationFn: () => emailConnectorApi.create({ name, provider }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['email-connectors'] }); onClose(); toast.success('Email connector created!'); },
    onError: (e) => toast.error((e as Error).message),
  });

  return (
    <div className={styles.drawerOverlay} onClick={onClose}>
      <div className={styles.drawer} onClick={e => e.stopPropagation()}>
        <div className={styles.drawerHeader}>
          <h2 className={styles.drawerTitle}>New Email Connector</h2>
          <button className={styles.drawerClose} onClick={onClose}><X size={18} /></button>
        </div>

        <div className={styles.drawerBody}>
          <label className={styles.fieldLabel}>
            Connector name
            <input className={styles.fieldInput} placeholder='e.g. "Support Inbox"' value={name} onChange={e => setName(e.target.value)} />
          </label>

          <p className={styles.fieldLabel} style={{ marginBottom: 8 }}>Provider</p>
          <div className={styles.providerPicker}>
            {(Object.keys(PROVIDER_META) as EmailConnectorProvider[]).map(p => {
              const meta = PROVIDER_META[p];
              return (
                <button
                  key={p}
                  className={`${styles.providerOption} ${provider === p ? styles.providerOptionSelected : ''}`}
                  style={provider === p ? { borderColor: meta.color } : undefined}
                  onClick={() => setProvider(p)}
                >
                  <span className={styles.providerOptionIcon}>{meta.icon(20)}</span>
                  <span className={styles.providerOptionLabel}>{meta.label}</span>
                  <span className={styles.providerOptionDesc}>{meta.description}</span>
                </button>
              );
            })}
          </div>

          <button
            className={styles.btnPrimary}
            style={{ marginTop: 16 }}
            disabled={!name || createMutation.isPending}
            onClick={() => createMutation.mutate()}
          >
            {createMutation.isPending ? <Spinner size="sm" /> : null}
            Create & Configure →
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Config / Setup drawer ─────────────────────────────────────────────────────

function ConfigDrawer({ connector, onClose }: { connector: EmailConnector; onClose: () => void }) {
  const meta = PROVIDER_META[connector.provider];
  return (
    <div className={styles.drawerOverlay} onClick={onClose}>
      <div className={styles.drawer} onClick={e => e.stopPropagation()}>
        <div className={styles.drawerHeader}>
          <div className={styles.drawerTitleRow}>
            <span className={styles.providerBadge} style={{ background: `${meta.color}22`, color: meta.color, borderColor: `${meta.color}44` }}>
              {meta.icon(14)} {meta.label}
            </span>
            <h2 className={styles.drawerTitle}>{connector.name}</h2>
          </div>
          <button className={styles.drawerClose} onClick={onClose}><X size={18} /></button>
        </div>
        <div className={styles.drawerBody}>
          {connector.provider === 'GMAIL'     && <GmailWizard connector={connector} onClose={onClose} />}
          {connector.provider === 'OUTLOOK'   && <OutlookWizard connector={connector} onClose={onClose} />}
          {connector.provider === 'IMAP_SMTP' && <ImapWizard connector={connector} onClose={onClose} />}
        </div>
      </div>
    </div>
  );
}

// ── Connector card ────────────────────────────────────────────────────────────

function ConnectorCard({ connector, onConfigure, onDelete }: {
  connector: EmailConnector;
  onConfigure: () => void;
  onDelete: () => void;
}) {
  const meta = PROVIDER_META[connector.provider];
  const isConnected = !!connector.connectedEmail;
  const isRunning = connector.consumerStatus === 'RUNNING';

  return (
    <div className={`${styles.card} ${!connector.active ? styles.cardInactive : ''}`}>
      <div className={styles.cardHeader}>
        <span className={styles.providerBadge} style={{ background: `${meta.color}22`, color: meta.color, borderColor: `${meta.color}44` }}>
          {meta.icon(13)} {meta.label}
        </span>
        <span className={`${styles.statusPill} ${connector.active ? styles.statusActive : styles.statusInactive}`}>
          <Circle size={6} fill="currentColor" />
          {connector.active ? 'Active' : 'Inactive'}
        </span>
      </div>

      <div className={styles.cardBrand}>{meta.icon(36)}</div>
      <h3 className={styles.cardName}>{connector.name}</h3>

      {connector.connectedEmail ? (
        <p className={styles.cardEmail}>{connector.connectedEmail}</p>
      ) : (
        <p className={styles.cardEmailUnset}>Not connected — setup required</p>
      )}

      <div className={styles.cardMeta}>
        <span className={`${styles.connectionPill} ${isConnected && isRunning ? styles.connectionLive : styles.connectionOff}`}>
          {isConnected && isRunning ? '● Live' : '○ Offline'}
        </span>
        <span className={styles.cardCount}>
          {connector._count?.workflows ?? 0} workflow{connector._count?.workflows !== 1 ? 's' : ''} linked
        </span>
      </div>

      {connector.lastEmailAt && (
        <p className={styles.cardTimestamp}>
          Last email: {new Date(connector.lastEmailAt).toLocaleString()}
        </p>
      )}

      <div className={styles.cardActions}>
        <button className={styles.btnSecondary} onClick={onDelete}><Trash2 size={13} /></button>
        <button className={styles.btnConfigure} onClick={onConfigure}>
          {isConnected ? 'Configure' : 'Set up'} <ChevronRight size={13} />
        </button>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

function EmailConnectorsPage() {
  const [showCreate, setShowCreate] = useState(false);
  const [configTarget, setConfigTarget] = useState<EmailConnector | null>(null);

  const qc = useQueryClient();
  const { data: connectors = [], isLoading } = useQuery({
    queryKey: ['email-connectors'],
    queryFn: () => emailConnectorApi.list(),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => emailConnectorApi.delete(id),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['email-connectors'] }); toast.success('Connector deleted'); },
    onError: (e) => toast.error((e as Error).message),
  });

  return (
    <div className={styles.page}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <div className={styles.headerIcon}><Mail size={20} /></div>
          <div>
            <h1 className={styles.pageTitle}>Email Connectors</h1>
            <p className={styles.pageSubtitle}>
              Receive inbound emails from Gmail, Outlook, or any IMAP mailbox. Emails create conversations and trigger workflows in real time.
            </p>
          </div>
        </div>
        <button className={styles.btnNew} onClick={() => setShowCreate(true)}>
          <Plus size={15} /> New Email Connector
        </button>
      </div>

      {/* Provider legend */}
      <div className={styles.legend}>
        <span className={styles.legendLabel}>Provider</span>
        {(Object.keys(PROVIDER_META) as EmailConnectorProvider[]).map(p => (
          <span key={p} className={styles.legendPill} style={{ color: PROVIDER_META[p].color, borderColor: `${PROVIDER_META[p].color}44` }}>
            {PROVIDER_META[p].icon(12)} {PROVIDER_META[p].label}
          </span>
        ))}
      </div>

      {/* Grid */}
      {isLoading ? (
        <div className={styles.loadingWrap}><Spinner /></div>
      ) : connectors.length === 0 ? (
        <div className={styles.empty}>
          <Mail size={40} opacity={0.3} />
          <p>No email connectors yet.</p>
          <button className={styles.btnNew} onClick={() => setShowCreate(true)}>
            <Plus size={14} /> New Email Connector
          </button>
        </div>
      ) : (
        <div className={styles.grid}>
          {connectors.map(c => (
            <ConnectorCard
              key={c.id}
              connector={c}
              onConfigure={() => setConfigTarget(c)}
              onDelete={() => { if (confirm(`Delete "${c.name}"?`)) deleteMutation.mutate(c.id); }}
            />
          ))}
        </div>
      )}

      {showCreate && <CreateDrawer onClose={() => setShowCreate(false)} />}
      {configTarget && <ConfigDrawer connector={configTarget} onClose={() => setConfigTarget(null)} />}
    </div>
  );
}
